import type { PublicCountryConfig } from './types.js';

// ── Web-safe subset of the UZ config ──────────────────────────────────────────
// Only the `PublicCountryConfig` fields (code, locales, defaultLocale, regions,
// currency, brand) live here — NOTHING Node-only. `public-registry.ts` imports
// ONLY this file, so the browser bundle it feeds can never transitively reach
// the classifier / companyId / sourceKeys that the full `uzConfig` (uz.ts) adds.
// The full config spreads `uzPublic` so these values stay defined once.

// Region names arrive from the org registry as raw Uzbek-Latin strings
// ("Toshkent shahri", "Surxondaryo viloyati"). They're a fixed set of 14
// oblasts + Tashkent city + Karakalpakstan, so a small normalized map localizes
// them to Russian. Uzbek keeps the source string as-is.
const RU_REGIONS: Record<string, string> = {
  'toshkent shahri': 'город Ташкент',
  'toshkent viloyati': 'Ташкентская область',
  'andijon viloyati': 'Андижанская область',
  'buxoro viloyati': 'Бухарская область',
  'fargona viloyati': 'Ферганская область',
  'jizzax viloyati': 'Джизакская область',
  'xorazm viloyati': 'Хорезмская область',
  'namangan viloyati': 'Наманганская область',
  'navoiy viloyati': 'Навоийская область',
  'qashqadaryo viloyati': 'Кашкадарьинская область',
  'qoraqalpogiston respublikasi': 'Республика Каракалпакстан',
  'samarqand viloyati': 'Самаркандская область',
  'sirdaryo viloyati': 'Сырдарьинская область',
  'surxondaryo viloyati': 'Сурхандарьинская область',
};

/** Normalize a raw region string for matching: lowercase, drop the various
 *  apostrophe glyphs (' ʻ ` ’) so "Farg'ona"/"Fargʻona" collapse to one key. */
function normalizeRegion(s: string): string {
  return s
    .toLowerCase()
    .replace(/['`ʻ’]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const uzPublic: PublicCountryConfig = {
  code: 'uz',
  locales: ['uz', 'ru'],
  defaultLocale: 'ru',
  regions: {
    /**
     * Localize a raw Uzbek-Latin region name. Russian → mapped name; Uzbek (or an
     * unrecognized string) → returned unchanged.
     *
     * ⚠️ Accepts the BARE stem too ("Namangan", "Qoraqalpog'iston"), not only the
     * registry's full form ("Namangan viloyati"). The `regions` table stores the
     * short name, so the buyer-region fallback that reads from a buyer's own
     * tenders feeds this the stem — which would otherwise miss the map and render
     * "Namangan" on a Russian page.
     */
    localize(name, locale) {
      if (!name) return null;
      if (locale !== 'ru') return name;
      const key = normalizeRegion(name);
      return (
        RU_REGIONS[key] ??
        RU_REGIONS[`${key} viloyati`] ??
        RU_REGIONS[`${key} respublikasi`] ??
        name
      );
    },
  },
  currency: {
    code: 'UZS',
    // ⚠️ No explicit UZS symbol/marker exists anywhere in format.ts today —
    // the currency word ("сум" / "so'm") is appended by CALLERS as plain copy,
    // never read back by the formatter. "so'm" is used here as the config's
    // canonical symbol per the seam contract; it is not (yet) consumed by
    // formatBudget/formatBudgetCompact.
    symbol: "so'm",
    // Moved verbatim from format.ts's COMPACT_UNITS / COMPACT_UNITS_UZ — same
    // strings, same order (largest magnitude first), pairing positionally
    // with the fixed [1e12, 1e9, 1e6, 1e3] threshold ladder in
    // `compactMagnitude`.
    compactUnits: {
      ru: ['трлн', 'млрд', 'млн', 'тыс.'],
      uz: ['trln', 'mlrd', 'mln', 'ming'],
    },
    // The currency word the UI shows next to amounts — the exact literals the
    // i18n strings used to hardcode inline, so UZ output is unchanged.
    words: { ru: 'сум', uz: "so'm" },
  },
  brand: {
    // Moved verbatim from the `SITE_NAME`/`SITE_URL` literals in seo.tsx and
    // prerender.ts. `supportEmail` had no constant of its own in either file —
    // it is the real support address (`COMPANY.email` in
    // apps/web/src/lib/company.ts, and the `SUPPORT_REPLY_TO` default across
    // services/api's notification senders): a live Workspace group, not a
    // placeholder.
    name: 'UzTender.com',
    siteUrl: 'https://uztender.com',
    supportEmail: 'support@uztender.com',
    // The ONE support identity on Telegram — the same handle `COMPANY_LEGAL.telegram`
    // and apps/web's `COMPANY.telegram` have published since 2026-09-20.
    supportTelegram: { handle: '@flesteyto', url: 'https://t.me/flesteyto' },
    // UZ publishes the operator's phone + WhatsApp line (unchanged).
    publishPhone: true,
    // The m16 teal — the exact literals tailwind.config.ts carried, and the
    // `:root` defaults in apps/web/src/styles/globals.css (a test holds the two
    // together), so the UZ site and letters are unchanged.
    palette: { primary: '#0a5c5c', mid: '#0d7070', light: '#e6f0f0' },
  },
  // Country name in the cases the copy uses — the exact literals the i18n
  // strings hardcoded, so UZ output is byte-identical.
  countryName: {
    ru: { nom: 'Узбекистан', gen: 'Узбекистана', prep: 'Узбекистане' },
    uz: { nom: "O'zbekiston", gen: "O'zbekistonning", prep: "O'zbekistonda" },
  },
  // Web ИНН form: UZ ИНН/СТИР is 9 digits (server keeps the full validate).
  companyIdForm: { label: 'ИНН', digits: 9 },
  phonePlaceholder: '+998 90 123-45-67',
  // The literal `IKPU_RE` both /results parsers carried (api results-search.ts,
  // web lib/results-search.ts), moved here byte for byte; uz.test.ts pins it.
  productCodePattern: /^\d{2}(\.\d{1,3}){0,4}$/,
  // Meta Pixel id — moved verbatim from the `fbq('init', …)` literal in
  // index.html (and the dead `META_PIXEL_ID` const in lib/tracking.ts). GA4 id
  // from the same index.html gtag config. The Vite build bakes both per country.
  analytics: { metaPixelId: '1194754032777047', ga4MeasurementId: 'G-06TE1QP5JR' },
  // UZ delivers alerts via Telegram (email is the fallback) and mounts the
  // Telegram login/connect widgets — the behaviour that predates this flag. The
  // bot and the channel are the ones the code named as literals before they
  // moved here; the first alert stays Telegram-only (see MessagingConfig).
  messaging: {
    telegram: true,
    telegramBot: 'uztender_com_bot',
    telegramChannel: { handle: '@uztender_uz', url: 'https://t.me/uztender_uz' },
    emailFirstAlert: false,
  },
  // UZ has the full ИКПУ classifier + years of BidHistory, so /market, /prices and
  // the goods/works/services kind filter all carry real data.
  // conversionLandings: /start and /ip are written for this market (unchanged).
  features: {
    priceIntel: true,
    teamPlans: true,
    corporateContactGate: true,
    conversionLandings: true,
    // ИИ-онбординг OFF: the Uzbek onboarding keeps its category picker exactly
    // as it was (the owner switched the feature on for Kazakhstan only, 15.10).
    aiOnboarding: false,
    // The pre-purchase teasers stay as they were (see the type); owner's call.
    honestTeasers: false,
    // cooperation.uz supply requests (`sourceKeys` has `cooperation`).
    supplyScope: true,
  },
  // The /ifi chips, in the order the page has always drawn them — every
  // institution Uzbekistan ingests, so the strip is unchanged here.
  ifiSources: ['worldbank', 'adb', 'ungm', 'undp', 'aiib', 'isdb', 'ebrd', 'uzrd', 'osce'],
  // No /catalog source chips on Uzbekistan: an empty list draws no strip and
  // makes the API ignore `?source=`, so the catalogue is exactly as it was.
  govSources: [],
  // What Uzbekistan had before this block existed, written down — nothing here
  // changes its checkout: the Payme card sheet, hosted Payme / Click / Octobank,
  // the yearly tab and the bank invoice. `ipPlan` stays true because the server
  // still lists the tier for its existing subscribers (the page hides the card).
  // The operator in the requisites takes the money itself, so no separate payee.
  payments: {
    checkout: 'local',
    yearlyPlans: true,
    bankInvoice: true,
    ipPlan: true,
    marks: ['payme', 'click', 'octobank', 'uzcard', 'humo', 'visa', 'mastercard'],
    cardPayee: null,
  },
};
